import React, { useEffect, useState, useMemo } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonBackButton,
  IonContent,
  IonList,
  IonItem,
  IonLabel,
  IonButton,
  IonIcon,
  IonInput,
  IonModal,
  IonFooter,
  IonText,
  IonSpinner,
  IonSearchbar,
  IonItemSliding,
  IonItemOptions,
  IonItemOption,
  IonAlert,
  IonActionSheet,
  IonChip,
  IonAvatar,
  IonSelect,
  IonSelectOption,
} from '@ionic/react';
import {
  addOutline,
  personOutline,
  trashOutline,
  createOutline,
  mailOutline,
  callOutline,
  logoWhatsapp,
  chatbubbleOutline,
  linkOutline,
  closeOutline,
  copyOutline,
} from 'ionicons/icons';
import { useAuth } from '@clerk/clerk-react';
import { useHistory } from 'react-router-dom';
import { apiUrl, apiHeaders } from '../lib/api';
import { useI18n } from '../i18n/I18nProvider';
import {
  findExistingDirectChatsForUser,
  startChatFromContact,
  type DirectChatRef,
} from '../lib/startChatFlow';
import PhoneInputField from '../components/PhoneInputField';
import {
  isPhoneOptionalValid,
  parseValidPhone,
  phoneCountryFromLocale,
} from '../lib/phone';

interface ContactData {
  id: string;
  name: string;
  linkedUserId: string | null;
  linkedUser: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    imageUrl: string | null;
  } | null;
  emails: string[];
  phones: string[];
  createdAt: string;
  updatedAt: string;
}

interface TopicSummary {
  id: string;
  name: string;
  role?: string | null;
}

interface InvitePayload {
  inviteUrl: string;
  message: string;
  topicName: string;
}

const ContactsPage: React.FC = () => {
  const { getToken } = useAuth();
  const history = useHistory();
  const { t, locale } = useI18n();
  const [phoneForceError, setPhoneForceError] = useState(false);

  const [contacts, setContacts] = useState<ContactData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  // Create/Edit modal
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState<ContactData | null>(null);
  const [formName, setFormName] = useState('');
  const [formEmails, setFormEmails] = useState<string[]>(['']);
  const [formPhones, setFormPhones] = useState<string[]>(['']);
  const [saving, setSaving] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<ContactData | null>(null);

  // Invite flow
  const [inviteContact, setInviteContact] = useState<ContactData | null>(null);
  const [topics, setTopics] = useState<TopicSummary[]>([]);
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null);
  const [invitePayload, setInvitePayload] = useState<InvitePayload | null>(null);
  const [showInviteSheet, setShowInviteSheet] = useState(false);
  const [copiedFeedback, setCopiedFeedback] = useState(false);
  const [messageNowContact, setMessageNowContact] = useState<ContactData | null>(
    null
  );
  const [existingChatPrompt, setExistingChatPrompt] = useState<{
    existing: DirectChatRef[];
    contact: ContactData;
  } | null>(null);
  const [joinInviteContact, setJoinInviteContact] = useState<{
    contact: ContactData;
    joinUrl: string;
    shareText: string;
  } | null>(null);
  const [showJoinInviteSheet, setShowJoinInviteSheet] = useState(false);

  const filteredContacts = useMemo(() => {
    if (!search.trim()) return contacts;
    const q = search.toLowerCase();
    return contacts.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.emails.some((e) => e.toLowerCase().includes(q)) ||
        c.phones.some((p) => p.includes(q))
    );
  }, [contacts, search]);

  useEffect(() => {
    loadContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadContacts() {
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      const res = await fetch(apiUrl('/api/contacts'), {
        headers: { 'Content-Type': 'application/json', ...apiHeaders(token) },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const data = (await res.json()) as ContactData[];
      setContacts(data);
    } catch {
      setError(t('contacts.error.loadFailed'));
    } finally {
      setLoading(false);
    }
  }

  function openCreateModal() {
    setEditingContact(null);
    setFormName('');
    setFormEmails(['']);
    setFormPhones(['']);
    setShowModal(true);
  }

  function openEditModal(contact: ContactData) {
    setEditingContact(contact);
    setFormName(contact.name);
    setFormEmails(contact.emails.length > 0 ? [...contact.emails] : ['']);
    setFormPhones(contact.phones.length > 0 ? [...contact.phones] : ['']);
    setShowModal(true);
  }

  async function handleSave() {
    if (!formName.trim()) {
      setError(t('contacts.modal.name.placeholder'));
      return;
    }

    const country = phoneCountryFromLocale(locale);
    const normalizedPhones: string[] = [];
    for (const raw of formPhones) {
      const trimmed = raw.trim();
      if (!trimmed) continue;
      if (!isPhoneOptionalValid(trimmed, country)) {
        setPhoneForceError(true);
        setError(t('phone.invalid'));
        return;
      }
      const e164 = parseValidPhone(trimmed, country);
      if (e164) normalizedPhones.push(e164);
    }

    try {
      setSaving(true);
      setError(null);
      setPhoneForceError(false);
      const token = await getToken();
      const body = {
        name: formName.trim(),
        emails: formEmails.filter((e) => e.trim()),
        phones: normalizedPhones,
      };

      const isEdit = !!editingContact;
      const url = isEdit
        ? apiUrl(`/api/contacts/${editingContact!.id}`)
        : apiUrl('/api/contacts');

      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', ...apiHeaders(token) },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const errBody = await res.json().catch(() => undefined);
        throw new Error(errBody?.message || t('contacts.error.saveFailed'));
      }

      const saved = (await res.json()) as ContactData;

      if (isEdit) {
        setContacts((prev) => prev.map((c) => (c.id === saved.id ? saved : c)));
        setShowModal(false);
      } else {
        setContacts((prev) =>
          [...prev, saved].sort((a, b) => a.name.localeCompare(b.name))
        );
        setShowModal(false);
        if (saved.emails.length > 0 || saved.phones.length > 0) {
          setMessageNowContact(saved);
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t('contacts.error.saveFailed'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      const token = await getToken();
      const res = await fetch(apiUrl(`/api/contacts/${deleteTarget.id}`), {
        method: 'DELETE',
        headers: { ...apiHeaders(token) },
      });
      if (!res.ok && res.status !== 204) throw new Error();
      setContacts((prev) => prev.filter((c) => c.id !== deleteTarget.id));
    } catch {
      setError(t('contacts.error.deleteFailed'));
    } finally {
      setDeleteTarget(null);
    }
  }

  async function loadDirectChats(): Promise<DirectChatRef[]> {
    const token = await getToken();
    const res = await fetch(apiUrl('/api/chat/conversations'), {
      headers: {
        'Content-Type': 'application/json',
        ...apiHeaders(token),
      },
    });
    if (!res.ok) return [];
    return (await res.json()) as DirectChatRef[];
  }

  async function runMessageContact(contact: ContactData, forceNew: boolean) {
    const token = await getToken();
    const result = await startChatFromContact(token, contact, false, {
      forceNew,
    });
    if (result.status === 'ready') {
      history.push(`/chat/${result.conversationId}`);
      return;
    }
    setJoinInviteContact({
      contact,
      joinUrl: result.joinUrl,
      shareText: result.shareText,
    });
    setShowJoinInviteSheet(true);
  }

  async function handleMessageContact(contact: ContactData) {
    try {
      setError(null);
      if (contact.linkedUserId) {
        const chats = await loadDirectChats();
        const existing = findExistingDirectChatsForUser(
          chats,
          contact.linkedUserId
        );
        if (existing.length > 0) {
          setExistingChatPrompt({ existing, contact });
          return;
        }
      }
      await runMessageContact(contact, false);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('chats.error.createFailed'));
    }
  }

  const formatChatCreatedAt = (iso: string) => {
    try {
      return new Date(iso).toLocaleString(locale, {
        dateStyle: 'medium',
        timeStyle: 'short',
      });
    } catch {
      return iso;
    }
  };

  async function openInviteFlow(contact: ContactData) {
    setInviteContact(contact);
    setSelectedTopicId(null);
    setInvitePayload(null);

    try {
      const token = await getToken();
      const res = await fetch(apiUrl('/api/chat/topics'), {
        headers: { 'Content-Type': 'application/json', ...apiHeaders(token) },
      });
      if (!res.ok) throw new Error();
      const data = (await res.json()) as TopicSummary[];
      // Only topics where the user can actually enroll invitees (OWNER/ADMIN).
      setTopics(
        data.filter(
          (topic) => topic.role === 'OWNER' || topic.role === 'ADMIN'
        )
      );
    } catch {
      setTopics([]);
    }
  }

  async function handleTopicSelected(topicId: string) {
    setSelectedTopicId(topicId);
    setError(null);
    try {
      const token = await getToken();
      const email = inviteContact?.emails?.[0]?.trim();
      if (!email) {
        throw new Error(t('contacts.invite.emailRequired'));
      }
      const res = await fetch(
        apiUrl(
          `/api/contacts/invite-payload/${topicId}?email=${encodeURIComponent(email)}`
        ),
        {
          headers: { 'Content-Type': 'application/json', ...apiHeaders(token) },
        }
      );
      if (!res.ok) {
        const body = await res.json().catch(() => undefined);
        throw new Error(body?.message || `Invite failed (${res.status})`);
      }
      const data = (await res.json()) as InvitePayload;
      setInvitePayload(data);
      setShowInviteSheet(true);
    } catch (err) {
      setInvitePayload(null);
      setError(err instanceof Error ? err.message : t('contacts.error.saveFailed'));
    }
  }

  function buildMailtoUrl(email: string, payload: InvitePayload) {
    const subject = encodeURIComponent(`Join ${payload.topicName}`);
    const body = encodeURIComponent(payload.message);
    return `mailto:${email}?subject=${subject}&body=${body}`;
  }

  function buildSmsUrl(phone: string, payload: InvitePayload) {
    const body = encodeURIComponent(payload.message);
    return `sms:${phone}?body=${body}`;
  }

  function buildWhatsAppUrl(payload: InvitePayload) {
    const text = encodeURIComponent(payload.message);
    return `https://wa.me/?text=${text}`;
  }

  async function handleCopyInviteLink() {
    if (!invitePayload) return;
    try {
      await navigator.clipboard.writeText(invitePayload.inviteUrl);
      setCopiedFeedback(true);
      setTimeout(() => setCopiedFeedback(false), 2000);
    } catch {
      // fallback: ignore
    }
  }

  function updateFormEmail(index: number, value: string) {
    setFormEmails((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  }

  function updateFormPhone(index: number, value: string) {
    setFormPhones((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  }

  function removeFormEmail(index: number) {
    setFormEmails((prev) => prev.filter((_, i) => i !== index));
  }

  function removeFormPhone(index: number) {
    setFormPhones((prev) => prev.filter((_, i) => i !== index));
  }

  return (
    <IonPage>
      <IonHeader translucent>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/dashboard" text={t('common.actions.back')} />
          </IonButtons>
          <IonTitle>{t('contacts.title')}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={openCreateModal}>
              <IonIcon slot="icon-only" icon={addOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent fullscreen className="ion-padding vault-screen">
        <IonSearchbar
          value={search}
          onIonInput={(e) => setSearch(e.detail.value ?? '')}
          placeholder={t('contacts.search.placeholder')}
          debounce={200}
        />

        {loading && (
          <div className="ion-text-center ion-padding">
            <IonSpinner name="crescent" />
          </div>
        )}

        {error && !loading && (
          <IonText color="danger">
            <p className="ion-padding-horizontal">{error}</p>
          </IonText>
        )}

        {!loading && !error && filteredContacts.length === 0 && (
          <IonText color="medium">
            <p className="ion-padding-horizontal">{t('contacts.empty')}</p>
          </IonText>
        )}

        <IonList className="vault-feed-list contacts-list">
          {filteredContacts.map((contact) => {
            const primaryEmail = contact.emails[0] ?? '';
            const primaryPhone = contact.phones[0] ?? '';
            const extraEmails =
              contact.emails.length > 1 ? ` +${contact.emails.length - 1}` : '';
            const extraPhones =
              contact.phones.length > 1 ? ` +${contact.phones.length - 1}` : '';

            return (
              <IonItemSliding key={contact.id}>
                <IonItem lines="none" className="vault-feed-item contacts-row">
                  <div className="contacts-row-body">
                    <IonAvatar className="contacts-avatar">
                      {contact.linkedUser?.imageUrl ? (
                        <img src={contact.linkedUser.imageUrl} alt="" />
                      ) : (
                        <div className="contacts-avatar-fallback">
                          <IonIcon icon={personOutline} />
                        </div>
                      )}
                    </IonAvatar>
                    <div className="contacts-row-content">
                      <p className="contacts-item-name">{contact.name}</p>
                      {(primaryEmail || primaryPhone) && (
                        <p
                          className="contacts-item-meta"
                          title={
                            primaryEmail
                              ? contact.emails.join(', ')
                              : contact.phones.join(', ')
                          }
                        >
                          {primaryEmail
                            ? `${primaryEmail}${extraEmails}`
                            : `${primaryPhone}${extraPhones}`}
                        </p>
                      )}
                      <div className="contacts-row-actions">
                        {contact.linkedUser && (
                          <IonChip
                            color="success"
                            className="contacts-linked-chip"
                          >
                            <IonIcon icon={linkOutline} />
                            <IonLabel>{t('contacts.linkedUser')}</IonLabel>
                          </IonChip>
                        )}
                        <div className="contacts-row-action-buttons">
                          <IonButton
                            fill="clear"
                            size="small"
                            aria-label={t('contacts.actions.message')}
                            onClick={() => void handleMessageContact(contact)}
                          >
                            <IonIcon
                              slot="icon-only"
                              icon={chatbubbleOutline}
                            />
                          </IonButton>
                          <IonButton
                            fill="clear"
                            size="small"
                            aria-label={t('contacts.invite.title', {
                              name: contact.name,
                            })}
                            onClick={() => openInviteFlow(contact)}
                          >
                            <IonIcon slot="icon-only" icon={linkOutline} />
                          </IonButton>
                          <IonButton
                            fill="clear"
                            size="small"
                            aria-label={t('contacts.modal.editContact.title')}
                            onClick={() => openEditModal(contact)}
                          >
                            <IonIcon slot="icon-only" icon={createOutline} />
                          </IonButton>
                          <IonButton
                            fill="clear"
                            size="small"
                            color="danger"
                            aria-label={t('contacts.delete.title')}
                            onClick={() => setDeleteTarget(contact)}
                          >
                            <IonIcon slot="icon-only" icon={trashOutline} />
                          </IonButton>
                        </div>
                      </div>
                    </div>
                  </div>
                </IonItem>

                <IonItemOptions side="end">
                  <IonItemOption
                    color="danger"
                    onClick={() => setDeleteTarget(contact)}
                  >
                    <IonIcon slot="icon-only" icon={trashOutline} />
                  </IonItemOption>
                </IonItemOptions>
              </IonItemSliding>
            );
          })}
        </IonList>
      </IonContent>

      {/* Create / Edit Modal */}
      <IonModal
        className="vault-glass-modal"
        isOpen={showModal}
        onDidDismiss={() => setShowModal(false)}
      >
        <IonHeader>
          <IonToolbar>
            <IonTitle>
              {editingContact
                ? t('contacts.modal.editContact.title')
                : t('contacts.modal.newContact.title')}
            </IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <IonItem lines="none" className="vault-field-item">
            <IonLabel position="stacked">{t('contacts.modal.name.label')}</IonLabel>
            <IonInput
              value={formName}
              onIonChange={(e) => setFormName(e.detail.value ?? '')}
              placeholder={t('contacts.modal.name.placeholder')}
            />
          </IonItem>

          <IonLabel className="ion-padding-start" style={{ fontWeight: 600, fontSize: 14 }}>
            {t('contacts.modal.emails.label')}
          </IonLabel>
          {formEmails.map((email, i) => (
            <IonItem key={`email-${i}`} lines="none" className="vault-field-item">
              <IonInput
                type="email"
                value={email}
                onIonChange={(e) => updateFormEmail(i, e.detail.value ?? '')}
                placeholder={t('contacts.modal.emails.placeholder')}
              />
              {formEmails.length > 1 && (
                <IonButton slot="end" fill="clear" size="small" onClick={() => removeFormEmail(i)}>
                  <IonIcon slot="icon-only" icon={closeOutline} />
                </IonButton>
              )}
            </IonItem>
          ))}
          {formEmails.length < 3 && (
            <IonButton
              fill="clear"
              size="small"
              onClick={() => setFormEmails((prev) => [...prev, ''])}
            >
              <IonIcon slot="start" icon={addOutline} />
              {t('contacts.modal.actions.addEmail')}
            </IonButton>
          )}

          <IonLabel
            className="ion-padding-start"
            style={{ fontWeight: 600, fontSize: 14, marginTop: 12, display: 'block' }}
          >
            {t('contacts.modal.phones.label')}
          </IonLabel>
          {formPhones.map((phone, i) => (
            <PhoneInputField
              key={`phone-${i}`}
              value={phone}
              onChange={(v) => updateFormPhone(i, v)}
              placeholder={t('contacts.modal.phones.placeholder')}
              showError={phoneForceError}
              clearable={formPhones.length > 1}
              onClear={() => removeFormPhone(i)}
            />
          ))}
          {formPhones.length < 3 && (
            <IonButton
              fill="clear"
              size="small"
              onClick={() => setFormPhones((prev) => [...prev, ''])}
            >
              <IonIcon slot="start" icon={addOutline} />
              {t('contacts.modal.actions.addPhone')}
            </IonButton>
          )}

          {error && (
            <IonText color="danger">
              <p className="ion-padding">{error}</p>
            </IonText>
          )}
        </IonContent>
        <IonFooter>
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton onClick={() => setShowModal(false)}>
                {t('contacts.modal.actions.cancel')}
              </IonButton>
            </IonButtons>
            <IonButtons slot="end">
              <IonButton onClick={handleSave} disabled={saving}>
                {saving ? (
                  <IonSpinner name="crescent" />
                ) : editingContact ? (
                  t('contacts.modal.actions.save')
                ) : (
                  t('contacts.modal.actions.create')
                )}
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonFooter>
      </IonModal>

      {/* Delete Confirmation */}
      <IonAlert
        isOpen={!!deleteTarget}
        header={t('contacts.delete.title')}
        message={t('contacts.delete.message', { name: deleteTarget?.name ?? '' })}
        buttons={[
          { text: t('contacts.delete.cancel'), role: 'cancel', handler: () => setDeleteTarget(null) },
          { text: t('contacts.delete.confirm'), role: 'destructive', handler: handleDelete },
        ]}
        onDidDismiss={() => setDeleteTarget(null)}
      />

      {/* Invite modal — group selection then channel picker */}
      <IonModal
        className="vault-glass-modal"
        isOpen={!!inviteContact && !showInviteSheet}
        onDidDismiss={() => setInviteContact(null)}
      >
        <IonHeader>
          <IonToolbar>
            <IonTitle>
              {t('contacts.invite.title', { name: inviteContact?.name ?? '' })}
            </IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          {topics.length === 0 ? (
            <IonText color="medium">
              <p>{t('contacts.invite.noTopics')}</p>
            </IonText>
          ) : (
            <>
              <IonItem lines="none" className="vault-field-item">
                <IonLabel position="stacked">{t('contacts.invite.selectTopic')}</IonLabel>
                <IonSelect
                  value={selectedTopicId}
                  onIonChange={(e) => handleTopicSelected(e.detail.value)}
                  placeholder={t('contacts.invite.selectTopic')}
                >
                  {topics.map((topic) => (
                    <IonSelectOption key={topic.id} value={topic.id}>
                      {topic.name}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>
              {error && (
                <IonText color="danger">
                  <p className="ion-padding-horizontal">{error}</p>
                </IonText>
              )}
            </>
          )}
        </IonContent>
        <IonFooter>
          <IonToolbar>
            <IonButtons slot="start">
              <IonButton onClick={() => setInviteContact(null)}>
                {t('contacts.modal.actions.cancel')}
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonFooter>
      </IonModal>

      {/* Invite channel action sheet */}
      <IonActionSheet
        isOpen={showInviteSheet && !!invitePayload && !!inviteContact}
        header={t('contacts.invite.title', { name: inviteContact?.name ?? '' })}
        onDidDismiss={() => {
          setShowInviteSheet(false);
          setInviteContact(null);
          setInvitePayload(null);
        }}
        buttons={[
          ...(inviteContact?.emails?.[0] && invitePayload
            ? [
                {
                  text: t('contacts.invite.email'),
                  icon: mailOutline,
                  handler: () => {
                    window.open(
                      buildMailtoUrl(inviteContact!.emails[0], invitePayload!),
                      '_blank'
                    );
                  },
                },
              ]
            : []),
          ...(inviteContact?.phones?.[0] && invitePayload
            ? [
                {
                  text: t('contacts.invite.sms'),
                  icon: callOutline,
                  handler: () => {
                    window.open(
                      buildSmsUrl(inviteContact!.phones[0], invitePayload!),
                      '_blank'
                    );
                  },
                },
              ]
            : []),
          ...(invitePayload
            ? [
                {
                  text: t('contacts.invite.whatsapp'),
                  icon: logoWhatsapp,
                  handler: () => {
                    window.open(buildWhatsAppUrl(invitePayload!), '_blank');
                  },
                },
                {
                  text: copiedFeedback
                    ? t('contacts.invite.copied')
                    : t('contacts.invite.copy'),
                  icon: copyOutline,
                  handler: () => {
                    handleCopyInviteLink();
                  },
                },
              ]
            : []),
          {
            text: t('contacts.modal.actions.cancel'),
            role: 'cancel',
          },
        ]}
      />

      <IonAlert
        isOpen={!!messageNowContact}
        header={t('contacts.messageNow.title')}
        message={t('contacts.messageNow.message', {
          name: messageNowContact?.name ?? '',
        })}
        buttons={[
          {
            text: t('contacts.messageNow.later'),
            role: 'cancel',
            handler: () => setMessageNowContact(null),
          },
          {
            text: t('contacts.messageNow.confirm'),
            handler: () => {
              const contact = messageNowContact;
              setMessageNowContact(null);
              if (contact) void handleMessageContact(contact);
            },
          },
        ]}
        onDidDismiss={() => setMessageNowContact(null)}
      />

      <IonAlert
        isOpen={!!existingChatPrompt}
        header={t('chats.existing.title')}
        message={
          existingChatPrompt
            ? existingChatPrompt.existing.length > 1
              ? t('chats.existing.messageMany', {
                  count: existingChatPrompt.existing.length,
                  date: formatChatCreatedAt(
                    existingChatPrompt.existing[0].createdAt
                  ),
                })
              : t('chats.existing.message', {
                  date: formatChatCreatedAt(
                    existingChatPrompt.existing[0].createdAt
                  ),
                })
            : ''
        }
        buttons={[
          {
            text: t('common.actions.cancel'),
            role: 'cancel',
            handler: () => setExistingChatPrompt(null),
          },
          {
            text: t('chats.existing.createNew'),
            handler: () => {
              const prompt = existingChatPrompt;
              setExistingChatPrompt(null);
              if (prompt) void runMessageContact(prompt.contact, true);
            },
          },
          {
            text: t('chats.existing.openExisting'),
            handler: () => {
              const id = existingChatPrompt?.existing[0]?.id;
              setExistingChatPrompt(null);
              if (id) history.push(`/chat/${id}`);
            },
          },
        ]}
        onDidDismiss={() => setExistingChatPrompt(null)}
      />

      <IonActionSheet
        isOpen={showJoinInviteSheet && !!joinInviteContact}
        header={t('chats.invite.neededTitle', {
          name: joinInviteContact?.contact.name ?? '',
        })}
        subHeader={t('chats.invite.neededBody')}
        onDidDismiss={() => {
          setShowJoinInviteSheet(false);
          setJoinInviteContact(null);
        }}
        buttons={[
          ...(joinInviteContact?.contact.emails[0]
            ? [
                {
                  text: t('contacts.invite.email'),
                  icon: mailOutline,
                  handler: () => {
                    const email = joinInviteContact!.contact.emails[0];
                    const subject = encodeURIComponent(
                      t('chats.invite.emailSubject')
                    );
                    const body = encodeURIComponent(
                      joinInviteContact!.shareText
                    );
                    window.open(
                      `mailto:${email}?subject=${subject}&body=${body}`,
                      '_blank'
                    );
                  },
                },
              ]
            : []),
          ...(joinInviteContact?.contact.phones[0]
            ? [
                {
                  text: t('contacts.invite.sms'),
                  icon: callOutline,
                  handler: () => {
                    const phone = joinInviteContact!.contact.phones[0];
                    const body = encodeURIComponent(
                      joinInviteContact!.shareText
                    );
                    window.open(`sms:${phone}?body=${body}`, '_blank');
                  },
                },
              ]
            : []),
          {
            text: t('contacts.invite.whatsapp'),
            icon: logoWhatsapp,
            handler: () => {
              if (!joinInviteContact) return;
              window.open(
                `https://wa.me/?text=${encodeURIComponent(joinInviteContact.shareText)}`,
                '_blank'
              );
            },
          },
          {
            text: t('contacts.invite.copy'),
            icon: copyOutline,
            handler: () => {
              if (!joinInviteContact) return;
              void navigator.clipboard.writeText(joinInviteContact.joinUrl);
            },
          },
          {
            text: t('contacts.modal.actions.cancel'),
            role: 'cancel',
          },
        ]}
      />
    </IonPage>
  );
};

export default ContactsPage;
